import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { firstValueFrom, BehaviorSubject, Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';

interface MaintenanceCache {
  value: boolean;
  timestamp: number;
}

@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private apiUrl = environment.apiNest;

  // Caché con TTL de 30 segundos (ajustable)
  private readonly CACHE_TTL_MS = 30000; // 30 segundos
  private cache: MaintenanceCache | null = null;

  // BehaviorSubject para compartir estado reactivo
  private maintenanceState$ = new BehaviorSubject<boolean>(false);

  // Flag para evitar peticiones duplicadas
  private requestInProgress = false;
  private pendingPromise: Promise<boolean> | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Obtiene el estado de mantenimiento con sistema de caché inteligente
   * - Retorna valor cacheado si no ha expirado
   * - Evita peticiones duplicadas simultáneas
   * - Actualiza BehaviorSubject para suscriptores reactivos
   */
  async isMaintenanceActive(): Promise<boolean> {
    // 1. Verificar si hay caché válido
    if (this.cache && this.isCacheValid()) {
      return this.cache.value;
    }

    // 2. Si hay una petición en progreso, esperar por ella
    if (this.requestInProgress && this.pendingPromise) {
      return this.pendingPromise;
    }

    // 3. Iniciar nueva petición
    this.requestInProgress = true;
    this.pendingPromise = this.fetchMaintenanceStatus();

    try {
      const result = await this.pendingPromise;
      return result;
    } finally {
      this.requestInProgress = false;
      this.pendingPromise = null;
    }
  }

  /**
   * Obtiene el Observable del estado de mantenimiento
   * Útil para componentes que quieren reaccionar a cambios
   */
  getMaintenanceState$(): Observable<boolean> {
    return this.maintenanceState$.asObservable();
  }

  /**
   * Fuerza una actualización del estado ignorando el caché
   * Útil para polling o cuando se necesita el valor más reciente
   */
  async forceRefresh(): Promise<boolean> {
    this.invalidateCache();
    return this.isMaintenanceActive();
  }

  /**
   * Invalida el caché manualmente
   */
  invalidateCache(): void {
    this.cache = null;
  }

  /**
   * Verifica si el caché es válido (no ha expirado)
   */
  private isCacheValid(): boolean {
    if (!this.cache) return false;
    const now = Date.now();
    const elapsed = now - this.cache.timestamp;
    return elapsed < this.CACHE_TTL_MS;
  }

  /**
   * Ejecuta la petición HTTP al backend
   */
  private async fetchMaintenanceStatus(): Promise<boolean> {
    try {
      const headers = new HttpHeaders().set('skip', 'true');

      // Timeout de cliente: si el backend cuelga el endpoint `/mantenimiento`
      // (no responde), no podemos dejar que la promesa quede colgada para
      // siempre bloqueando el arranque de la app. A los 5s cortamos y el
      // `catch` de abajo asume "no hay mantenimiento".
      const res = await firstValueFrom(
        this.http
          .get<{ valor: boolean }>(`${this.apiUrl}mantenimiento`, {
            headers,
          })
          .pipe(timeout(5000)),
      );

      const isActive = res.valor === true;

      // Actualizar caché
      this.cache = {
        value: isActive,
        timestamp: Date.now(),
      };

      // Actualizar BehaviorSubject
      this.maintenanceState$.next(isActive);

      return isActive;
    } catch (error) {

      // En caso de error, retornar el valor cacheado si existe
      if (this.cache) {
        return this.cache.value;
      }

      // Si no hay caché, asumir que NO está en mantenimiento
      return false;
    }
  }

  /**
   * Obtiene el valor actual del caché sin hacer peticiones
   * Retorna null si no hay caché
   */
  getCachedValue(): boolean | null {
    return this.cache?.value ?? null;
  }

  /**
   * Obtiene información sobre el estado del caché
   */
  getCacheInfo(): { hasCache: boolean; isValid: boolean; age: number | null } {
    if (!this.cache) {
      return { hasCache: false, isValid: false, age: null };
    }

    const age = Date.now() - this.cache.timestamp;
    const isValid = age < this.CACHE_TTL_MS;

    return { hasCache: true, isValid, age };
  }
}
